import { useCallback, useEffect, useState } from 'react';
import { Theme, ThemeProvider } from '@emotion/react';
import { ErrorBoundary } from '@sentry/react';
import merge from 'lodash/merge';

import { bridgeAtom, Provider, store, useAtom, useReset } from '@/store';

import { AppEvents, getProductUrl, parseShopifyUrl } from '@/utils';

import { request } from '@/modules/api';
import { theme as defaultTheme, useEvent } from '@/modules/common';

import { App } from './App';

import type { ApiAppParams } from 'typings/apiApp';

if (process.env.NODE_ENV === 'production') {
  import('@/utils/sentry');
}

type AppContainerProps = { config: ApiAppParams };

function AppContainer(props: AppContainerProps) {
  const [theme, setTheme] = useState<Theme>(merge({}, defaultTheme, props.config.theme));

  const resetBridgeAtom = useReset(bridgeAtom);
  const [bridge, setBridge] = useAtom(bridgeAtom);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');

    if (testParam === '1') {
      localStorage.setItem('testUser', '1');
    }
  }, []);

  const getBridgeParams = useCallback((config?: Partial<ApiAppParams>) => {
    const { apiUrl, theme, pageType: bridgePageType, ...others } = merge({}, props.config, config);
    request.setBaseUrl(apiUrl);

    setTheme((p) => merge({}, p, theme));

    const url = getProductUrl();
    const { productId, variantId, pageType } = parseShopifyUrl(url);

    setBridge((prev) => {
      return merge({}, prev, others, { apiUrl, pageType: bridgePageType ?? pageType, productId, variantId });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResetParams = useCallback(() => {
    resetBridgeAtom();
    getBridgeParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdateParams = useCallback(
    (params: CustomEvent<Partial<ApiAppParams>>) => {
      getBridgeParams(params.detail);
    },
    [getBridgeParams]
  );

  useEffect(() => {
    getBridgeParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEvent({ name: AppEvents.UPDATE_PARAMS, handler: onUpdateParams });
  useEvent({ name: AppEvents.RESET_CONFIG, handler: onResetParams });

  if (!bridge.pageType || !theme) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default function AppContainerProvider(props: AppContainerProps) {
  return (
    <Provider store={store}>
      <AppContainer {...props} />
    </Provider>
  );
}
